import { BaseEditor } from 'handsontable/editors';
import { EDITOR_STATE } from 'handsontable/editors/baseEditor';
import type { CellProperties } from 'handsontable/settings';

import type { CellData } from '../../../../common/table/TableTypes';
import type { MNHotInstance } from '../tableHotInstanceTypes';
import { getCellBackgroundColor } from '../utils/dataUtils/cell/tableCellStyleUtils';
import { isSelectingSingleSelectionAndCell } from '../utils/selectionUtils/tableSelectionPredicateUtils';
import { isHotInstanceAvailable } from '../utils/hotInstanceUtils/hotTableInstanceValidationUtils';

// Constants
import { toRendererHeight } from '../utils/hotInstanceUtils/cell/hotTableCellEditorHeightUtils';
import MilanoteCellEditorLayoutManager from '../manager/MilanoteCellEditorLayoutManager';
import EditorShortcutsHotPlugin, { EDITOR_SHORTCUTS_PLUGIN_NAME } from '../plugins/EditorShortcutsHotPlugin';
import { isFormula } from '../utils/dataUtils/formula/tableFormulaPredicateUtils';
import type { EditorState } from 'draft-js';

export default abstract class MilanoteCellEditor<Content extends EditorState> extends BaseEditor {
    protected cellEditorLayoutManager: MilanoteCellEditorLayoutManager;

    constructor(instance: MNHotInstance) {
        super(instance);

        this.cellEditorLayoutManager = new MilanoteCellEditorLayoutManager({
            hotInstance: this.milanoteHot,

            // On an editor height change, call this.milanoteHot.render(), so that the renderer height
            // will be recalculated in modifyRowHeight and rerendered.
            onEditorHeightChange: (height) => {
                if (isHotInstanceAvailable(this.milanoteHot) && height !== null) this.milanoteHot.render();
            },
        });

        this.bindEvents();
    }

    get milanoteHot(): MNHotInstance {
        return this.hot as MNHotInstance;
    }

    bindEvents(): void {
        this.milanoteHot.addHook('afterScrollHorizontally', () => this.initEditorPositionAndSize());
        this.milanoteHot.addHook('afterScrollVertically', () => this.initEditorPositionAndSize());

        this.milanoteHot.addHook('afterColumnResize', () => this.initEditorPositionAndSize());
        this.milanoteHot.addHook('afterRowResize', () => this.initEditorPositionAndSize());
        this.milanoteHot.addHook('afterFormulasValuesUpdate', () => setTimeout(() => this.initEditorPositionAndSize()));
        this.milanoteHot.addHook('onMilanoteCellEditorChange', (content) =>
            this.onMilanoteCellEditorChange(content as Content),
        );

        this.milanoteHot.addHook('modifyRowHeight', (height, row) => this.modifyRowHeight(height, row));
    }

    /**
     * 1. Called when a cell is selected (not in edit mode yet), to prepare it for edit mode
     */
    prepare(
        row: number,
        col: number,
        prop: string | number,
        TD: HTMLTableCellElement,
        originalValue: unknown,
        cellProperties: CellProperties & { cellData: CellData },
    ): void {
        super.prepare(row, col, prop, TD, originalValue, cellProperties);

        if (!this.cellEditorLayoutManager.initEditorElements(this.milanoteHot.rootElement)) return;

        this.cellEditorLayoutManager.setBackgroundColor(getCellBackgroundColor(cellProperties.cellData));

        this.initEditorPositionAndSize();
    }

    /**
     * 2. Called when the cell enters edit mode
     */
    beginEditing(initialValue?: string, event?: Event): void {
        if (
            !isSelectingSingleSelectionAndCell(this.milanoteHot.getSelected()) ||
            this.milanoteHot.milanoteProps.isResizing || // don't allow cell edits while resizing to avoid syncing/saving issues
            this.state !== EDITOR_STATE.VIRGIN
        )
            return;

        super.beginEditing(initialValue, event);

        const cellCoords = { row: this.row, col: this.col };
        this.milanoteHot.milanoteProps.startEditingCellEditor({
            cellCoords,
            initialValue,
        });

        this.onEditorBeginEditing(initialValue, event);
    }

    /**
     * 3. Called to set a value inside editor if required
     *
     *    Do nothing here because onMilanoteCellEditorChange will be called when milanote editor going to editing mode,
     *    which will sync the content of the editor to this class
     */
    setValue(_value?: unknown): void {
        // Do nothing
    }

    /**
     * 4. Called right after beginEditing, to open the editor
     */
    open(): void {
        this.cellEditorLayoutManager.showEditor();

        // Hide contents of the current TD when it is being edited.
        // This is so that the TD can be dynamically resized to the size of the editor
        this.hideTdContent();

        const editingShortcutsPlugin = this.milanoteHot.getPlugin(
            EDITOR_SHORTCUTS_PLUGIN_NAME,
        ) as EditorShortcutsHotPlugin;
        editingShortcutsPlugin?.registerShortcuts();

        this.cellEditorLayoutManager.syncEditorHeight();
    }

    /**
     * 5. Called by MilanoteCelLEditorComponent on every change to the editor. This is to sync the changes in this class
     */
    onMilanoteCellEditorChange(content: Content): void {
        this.onEditorChange(content);
        this.cellEditorLayoutManager.syncEditorHeight();
    }

    /**
     * 6. Called before the cell exits edit mode, the return value of it will be the value used by Handsontable in
     *    the beforeChange hook
     */
    getValue(): string {
        const value = this.getEditorValue();
        if (!isFormula(value)) return value;

        const isOpenFormula = value.includes('(') && !value.includes(')');
        if (isOpenFormula) return `${value})`;

        return value;
    }

    /**
     * 7. Called as user finish editing a cell
     */
    finishEditing(restoreOriginalValue?: boolean, ctrlDown?: boolean, callback?: () => void): void {
        this.onEditorFinishEditing();

        super.finishEditing(restoreOriginalValue, ctrlDown, callback);

        this.milanoteHot.milanoteProps.finishEditingCellEditor();
    }

    /**
     * 8. Called when the cell exits edit mode
     */
    close(): void {
        this.cellEditorLayoutManager.hideEditor();

        this.showTdContent();

        this.clearEditorContent();

        const editingShortcutsPlugin = this.milanoteHot.getPlugin(
            EDITOR_SHORTCUTS_PLUGIN_NAME,
        ) as EditorShortcutsHotPlugin;
        editingShortcutsPlugin?.unregisterShortcuts();

        this.cellEditorLayoutManager.clearTrackedEditorHeight();
    }

    cancelChanges(): void {
        this.cellEditorLayoutManager.clearTrackedEditorHeight();

        super.cancelChanges();
    }

    focus(): void {}

    /**
     * This is a Handsontable hook called when this.milanoteHot.render() is called,
     * it returns the height that will be set on the cell renderer.
     *
     * When editor height is updated, make sure to update the renderer height to match editor.
     */
    modifyRowHeight(height: number, row: number): number {
        if (row !== this.row) return height;
        if (this.cellEditorLayoutManager.editorHeight === null) return height;

        return toRendererHeight(this.cellEditorLayoutManager.editorHeight, row);
    }

    /******************
     * HELPER FUNCTIONS
     ******************/

    abstract onEditorBeginEditing(initialValue?: string, event?: Event): void;

    abstract onEditorChange(editorChange: Content): void;

    abstract getEditorValue(): string;

    abstract onEditorFinishEditing(): void;

    abstract clearEditorContent(): void;

    abstract setPlainTextContent(plainText: string, styles?: string[]): void;
    abstract getPlainTextContent(): string;

    abstract setEditorState(editorState: Content): void;

    abstract hasValidElement(): boolean;

    initEditorPositionAndSize(): void {
        // This check is required as this.TD is used by this.getEditedCellRect(). Without this check,
        // this.getEditedCellRect() can sometimes crash the element.
        if (!this.TD || !isHotInstanceAvailable(this.milanoteHot)) return;

        const editedCellRect = this.getEditedCellRect();
        if (!editedCellRect) return;

        this.cellEditorLayoutManager.initEditorPositionAndSize(this.row, this.col, editedCellRect);
    }

    hideTdContent(): void {
        this.TD.firstElementChild?.classList.add('hidden');
    }

    showTdContent(): void {
        this.TD.firstElementChild?.classList.remove('hidden');
    }
}
